import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  withStyles
} from '@material-ui/core';
// Removed notistack dependency - enqueueSnackbar passed as prop
import { createColorMap, getStackColor } from './QuickNotesColorPalette';
import { cleanAggregatedChartDataWithColors } from './CategoryUtils';

// GraphQL query for monthly owner aggregates
const GET_MONTHLY_BY_OWNER = gql`
  query GetQuickNotesMonthlyByOwner(
    $month: String
    $stack: QuickNotesStackEnum!
    $startDate: String
    $endDate: String
  ) {
    getQuickNotesMonthlyByOwner(
      month: $month
      stack: $stack
      startDate: $startDate
      endDate: $endDate
    ) {
      ownerId
      ownerName
      buckets {
        key
        count
        timeSpentMinutes
      }
      totals {
        count
        timeSpentMinutes
      }
    }
  }
`;

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
  },
  filtersContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  monthSelector: {
    minWidth: 200,
  },
  chartContainer: {
    height: 400,
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  emptyState: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
});

const QuickNotesOwnerChart = ({ 
  metric = 'count',
  stack = 'ACTIVITY_TYPE',
  onDrilldown,
  onError,
  refreshTrigger = 0,
  classes,
  enqueueSnackbar,
  dateRange,
  selectedPeriod
}) => {
  
  // Convert container's metric format to component format
  const selectedMetric = metric;
  // Use stack as-is since container already provides correct GraphQL enum format
  const selectedStack = stack;
  // Use dateRange from props (month is deprecated)
  const selectedMonth = null; // No longer using month, using dateRange instead

  // Format date range label - must be before any conditional returns
  const selectedPeriodLabel = useMemo(() => {
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      try {
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        const startFormatted = startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        const endFormatted = endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        return `${startFormatted} - ${endFormatted}`;
      } catch (error) {
        return 'Invalid Date Range';
      }
    }
    return 'No Date Range';
  }, [dateRange]);

  // Use date range for filtering
  const queryVariables = {
    stack: selectedStack,
    startDate: dateRange?.startDate || null,
    endDate: dateRange?.endDate || null
  };

  const { data, loading, error, refetch } = useQuery(GET_MONTHLY_BY_OWNER, {
    variables: queryVariables,
    errorPolicy: 'all',
    onError: (error) => {
      console.error('QuickNotesOwnerChart: GraphQL error', error);
      enqueueSnackbar(`Error loading owner data: ${error.message}`, { variant: 'error' });
      
      // Propagate error to container if handler provided
      if (onError) {
        onError(error, `loading Owner chart data for ${selectedPeriodLabel}`);
      }
    },
    onCompleted: (data) => {
      // Data loaded successfully
    }
  });

  // Refetch data when month or stack dimension changes
  useEffect(() => {
    if (refetch) {
      refetch({
        month: selectedMonth,
        stack: selectedStack,
        order_by: 'owner_name',
        order: 'asc'
      });
    }
  }, [dateRange, selectedStack, refreshTrigger, refetch]);



  // Transform data for Recharts
  const transformDataForChart = (rawData) => {
    if (!rawData || !rawData.getQuickNotesMonthlyByOwner) return { transformedData: [], colorMap: {} };

    // Use original data without cleaning (like By Type chart)
    const aggregates = rawData.getQuickNotesMonthlyByOwner;
    const colorMap = createColorMap(aggregates, selectedStack);

    const transformedData = aggregates.map(ownerData => {
      const chartDataPoint = {
        ownerId: ownerData.ownerId,
        ownerName: ownerData.ownerName,
        // Truncate long names for better chart display
        displayName: ownerData.ownerName.length > 15 
          ? ownerData.ownerName.substring(0, 12) + '...' 
          : ownerData.ownerName,
        total: selectedMetric === 'count' ? ownerData.totals.count : ownerData.totals.timeSpentMinutes,
      };

      // Add each bucket as a separate data point for stacking (using original keys)
      ownerData.buckets.forEach(bucket => {
        const value = selectedMetric === 'count' ? bucket.count : bucket.timeSpentMinutes;
        chartDataPoint[bucket.key] = value; // Use original bucket.key directly
      });

      return chartDataPoint;
    });

    return { transformedData, colorMap };
  };

  // Get all unique stack keys for creating Bar components
  const getStackKeys = (transformedData) => {
    const keys = new Set();
    transformedData.forEach(dataPoint => {
      Object.keys(dataPoint).forEach(key => {
        if (!['ownerId', 'ownerName', 'displayName', 'total'].includes(key)) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const unit = selectedMetric === 'count' ? 'notes' : 'minutes';
      const fullName = payload[0]?.payload?.ownerName || label;

      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Typography variant="subtitle2" style={{ marginBottom: '4px' }}>
            {fullName}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" style={{ color: entry.color }}>
              {entry.dataKey}: {entry.value} {unit}
            </Typography>
          ))}
          <Typography variant="body2" style={{ fontWeight: 600, marginTop: '4px' }}>
            Total: {total} {unit}
          </Typography>
        </div>
      );
    }
    return null;
  };

  // Handle bar click for drill-down - for stacked bars, use click coordinates
  const handleBarClick = (data, event) => {
    if (onDrilldown && data && data.activePayload && data.activePayload.length > 0) {
      const ownerData = data.activePayload[0].payload;
      
      // For stacked bars, calculate which segment was clicked based on Y coordinate
      let stackKey = null;
      
      if (data.activePayload && data.chartY !== undefined) {
        // More precise coordinate calculation for small segments
        let cumulativeHeight = 0;
        const totalHeight = data.activePayload.reduce((sum, item) => sum + (item.value || 0), 0);
        
        // Get the actual bar dimensions from the chart
        const chartHeight = 200; // More accurate chart height
        const barStartY = 50; // Approximate Y offset where bars start
        const barHeight = chartHeight - barStartY;
        
        // Convert click Y to relative position within the bar (0 = bottom, totalHeight = top)
        const relativeClickY = Math.max(0, (barHeight - (data.chartY - barStartY)) / barHeight) * totalHeight;
        
        // Find the segment that contains the click Y coordinate
        for (const item of data.activePayload) {
          const segmentHeight = (item.value || 0);
          const segmentStartY = cumulativeHeight;
          const segmentEndY = cumulativeHeight + segmentHeight;
          
          // Add small tolerance for very small segments
          const tolerance = Math.min(segmentHeight * 0.1, totalHeight * 0.05);
          
          if (relativeClickY >= (segmentStartY - tolerance) && relativeClickY < (segmentEndY + tolerance)) {
            stackKey = item.dataKey;
            break;
          }
          
          cumulativeHeight += segmentHeight;
        }
        
        // Fallback: use the first non-zero segment
        if (!stackKey) {
          const fallbackSegment = data.activePayload.find(item => item.value > 0) || data.activePayload[0];
          stackKey = fallbackSegment.dataKey;
        }
      }
      
      // For owner charts, the stackKey should be based on the stack dimension:
      // - If stack is 'CATEGORY', use the original category name (if available)
      // - Otherwise, use the clicked bar's dataKey (activity type, planned status, etc.)
      if (selectedStack === 'CATEGORY' && ownerData.categoryName) {
        stackKey = ownerData.categoryName; // Use original category name from database
      }
      
      
      const drilldownData = {
        dateRange: dateRange,
        stackDimension: selectedStack,
        stackKey: stackKey,
        ownerId: ownerData.ownerId,
        ownerName: ownerData.ownerName,
        metric: selectedMetric
      };
      
      onDrilldown(drilldownData);
    }
  };



  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <Typography color="error" align="center">
            Error loading owner chart data. Please try again.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const chartResult = transformDataForChart(data);
  const transformedData = chartResult.transformedData || [];
  const colorMap = chartResult.colorMap || {};
  
  const stackKeys = getStackKeys(transformedData);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.cardHeader}>
          <Typography variant="h6" component="h2" id="owner-chart-title">
            Quick Notes Activity by Owner
          </Typography>
          <Typography variant="body2" color="textSecondary" id="owner-chart-description">
            {selectedMetric === 'count' ? 'Number of notes' : 'Total time spent (minutes)'} by team member for {selectedPeriodLabel}
          </Typography>
        </Box>



        {transformedData.length === 0 ? (
          <div className={classes.emptyState} role="status" aria-live="polite">
            <Typography variant="body1" component="p">
              No Quick Notes data available for {selectedPeriodLabel}.
            </Typography>
            <Typography variant="body2" style={{ marginTop: '8px' }} color="textSecondary">
              Try selecting a different month or check if team members have logged any activities.
            </Typography>
          </div>
        ) : (
          <div className={classes.chartContainer} role="img" aria-labelledby="owner-chart-title" aria-describedby="owner-chart-description">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={transformedData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60, // Extra space for rotated labels
                }}
                onClick={handleBarClick}
                accessibilityLayer
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="displayName"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {stackKeys.map((key) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="stack"
                    fill={colorMap[key]}
                    name={key}
                    cursor="pointer"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

QuickNotesOwnerChart.propTypes = {
  metric: PropTypes.oneOf(['count', 'time_spent']),
  stack: PropTypes.oneOf(['ACTIVITY_TYPE', 'PLANNED', 'CATEGORY']),
  onDrilldown: PropTypes.func,
  onError: PropTypes.func,
  refreshTrigger: PropTypes.number,
  classes: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  selectedPeriod: PropTypes.string,
};

export default withStyles(styles)(QuickNotesOwnerChart);